import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/useAuth";
import {
  LayoutDashboard, FileText, FolderOpen, User, MessageCircle,
  BadgeEuro, Users, LogOut, Newspaper, ListTree, Calculator, Coins, RefreshCw, Send, ChevronDown,
  Undo2, LifeBuoy, UserPlus, KeyRound, Flag, Network, Building2, QrCode, PanelsTopLeft,
} from "lucide-react";
import { SegnalaDialog } from "./SegnalaDialog";
import { FaTelegramPlane, FaWhatsapp } from "react-icons/fa";
import { cn } from "@/lib/utils";
import { useNewsUnread } from "@/hooks/useNewsUnread";
import { useSupportOptional } from "@/support/SupportContext";
import { useWhatsappCanale } from "@/hooks/useWhatsappCanale";
import { usePreventivoAttivo } from "@/hooks/usePreventivoAttivo";
import { useQrAgenziaAttivo } from "@/hooks/useQrAgenziaAttivo";
import type { VoceMenu } from "@/types";

interface NavItem {
  to?: string;
  label: string;
  icon: React.ReactNode;
  href?: string;
  tourKey?: string;
  badge?: number;
  children?: NavItem[];
}

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps) {
  const { user, logout, hasRole } = useAuth();
  const newsUnread = useNewsUnread();
  // A kill-switch spento la voce "Supporto live" deve essere invisibile (spec).
  const supportoAttivo = Boolean(useSupportOptional()?.enabled);
  const location = useLocation();
  const [manualExpanded, setManualExpanded] = useState<Record<string, boolean>>({});
  const [segnalaAperto, setSegnalaAperto] = useState(false);
  const isStaff = Boolean(user) && (hasRole("affiliato") || hasRole("operatore"));
  const waCanale = useWhatsappCanale(isStaff);
  // Le voci dell'agenzia arrivano con l'utente; la chiamata separata del Preventivo resta solo come
  // ripiego quando il dato manca (spec voci del menù 2026-10-31).
  const voci = user?.vociMenu ?? null;
  const preventivoDaHook = usePreventivoAttivo(isStaff && !voci);
  const qrAttivo = useQrAgenziaAttivo(Boolean(user) && hasRole("affiliato"));
  if (!user) return null;
  const accesa = (v: VoceMenu) => !voci || voci[v];
  const preventivoAttivo = voci ? voci.preventivo : preventivoDaHook;

  const showNews = hasRole("affiliato") || hasRole("operatore");
  const novitaItem: NavItem = {
    to: "/news",
    label: "Novità",
    icon: <Newspaper size={17} />,
    badge: newsUnread,
    tourKey: "news",
  };

  const clienteItems: NavItem[] = [
    { to: "/dashboard", label: "Dashboard", icon: <LayoutDashboard size={17} /> },
    { to: "/chat", label: "Consulenza", icon: <MessageCircle size={17} /> },
    { to: "/bollette", label: "Bollette", icon: <FileText size={17} /> },
    { to: "/pratiche", label: "Pratiche", icon: <FolderOpen size={17} /> },
    { to: "/profilo", label: "Profilo", icon: <User size={17} /> },
  ];

  // Tre gruppi divisi da una linea, senza titoli (Alfonso 2026-10-01): il lavoro
  // di tutti i giorni, i canali per lavorare da fuori, notizie e aiuto.
  const agenziaLavoro: NavItem[] = [
    ...(accesa("dashboard")
      ? [{ to: "/agenzia/dashboard", label: "Dashboard", icon: <LayoutDashboard size={17} />, tourKey: "dashboard" }]
      : []),
    ...(preventivoAttivo
      ? [{ to: "/preventivo-condomini", label: "Preventivo condomini", icon: <Building2 size={17} /> } as NavItem]
      : []),
    ...(accesa("consulenza")
      ? [{ to: "/agenzia/chat", label: "Consulenza", icon: <MessageCircle size={17} />, tourKey: "chat" }]
      : []),
    ...(accesa("pratiche")
      ? [{ to: "/agenzia/pratiche", label: "Pratiche", icon: <FolderOpen size={17} />, tourKey: "pratiche" }]
      : []),
    ...(accesa("clienti")
      ? [{ to: "/agenzia/clienti", label: "Clienti", icon: <Users size={17} />, tourKey: "clienti" }]
      : []),
  ];

  const agenziaCanali: NavItem[] = [
    ...(qrAttivo
      ? [{ to: "/agenzia/qr", label: "QR Self Service Clienti", icon: <QrCode size={17} /> } as NavItem]
      : []),
    ...(accesa("telegram")
      ? [{ to: "_telegram", label: "Telegram", icon: <FaTelegramPlane size={17} />, href: "https://t.me/aienergiabot", tourKey: "telegram" }]
      : []),
    ...(waCanale.abilitato && waCanale.link
      ? [{
          to: "_whatsapp",
          label: "WhatsApp",
          icon: <FaWhatsapp size={17} />,
          href: waCanale.link,
          tourKey: "whatsapp",
        } as NavItem]
      : []),
  ];

  const agenziaAiuto: NavItem[] = [
    ...(showNews ? [novitaItem] : []),
    ...(supportoAttivo
      ? [
          {
            to: "/supporto",
            label: "Supporto live",
            icon: <LifeBuoy size={17} />,
            tourKey: "supporto",
          },
        ]
      : []),
    { to: "/agenzia/profilo", label: "Profilo", icon: <User size={17} />, tourKey: "profilo" },
  ];

  const monitoraggioItems: NavItem[] = [
    { to: "/affiliato/dashboard", label: "Le mie performance", icon: <LayoutDashboard size={17} /> },
    { to: "/affiliato/commissioni", label: "Commissioni", icon: <BadgeEuro size={17} /> },
  ];

  // Responsabile di rete: legge le agenzie in carico, non inserisce pratiche —
  // niente chat, niente Telegram/WhatsApp, niente «Segnala un problema».
  const reteItems: NavItem[] = [
    { to: "/rete/dashboard", label: "Cruscotto", icon: <Network size={17} /> },
    { to: "/rete/agenzie", label: "Agenzie", icon: <Building2 size={17} /> },
    { to: "/rete/pratiche", label: "Pratiche", icon: <FolderOpen size={17} /> },
    { to: "/rete/clienti", label: "Clienti", icon: <Users size={17} /> },
    { to: "/rete/quota", label: "Quota", icon: <BadgeEuro size={17} /> },
    { to: "/rete/affiliazioni", label: "Affiliazioni", icon: <UserPlus size={17} /> },
    { to: "/rete/profilo", label: "Profilo", icon: <User size={17} /> },
  ];

  const sections: { label?: string; items: NavItem[]; richiudibile?: boolean; separata?: boolean }[] = [];

  if (hasRole("cliente")) {
    sections.push({ items: clienteItems });
  }
  if (isStaff) {
    sections.push({
      label: hasRole("cliente") ? "Agenzia" : undefined,
      items: agenziaLavoro,
    });
    sections.push({ items: agenziaCanali, separata: true });
    sections.push({ items: agenziaAiuto, separata: true });
  }
  if (hasRole("affiliato")) {
    sections.push({
      label: "Monitoraggio",
      items: monitoraggioItems,
    });
  }
  if (hasRole("responsabile")) {
    sections.push({ label: "Rete", items: reteItems });
  }
  if (hasRole("admin")) {
    sections.push({
      label: "Amministrazione",
      richiudibile: true,
      items: [
        {
          label: "Listini",
          icon: <ListTree size={17} />,
          children: [
            { to: "/admin/listino", label: "Listino", icon: <ListTree size={17} /> },
            { to: "/admin/simulatore", label: "Simulatore", icon: <Calculator size={17} /> },
            { to: "/admin/commissioni", label: "Commissioni", icon: <Coins size={17} /> },
            { to: "/admin/reconcile", label: "Ricalcolo commissioni", icon: <RefreshCw size={17} /> },
            { to: "/admin/storni", label: "Storni", icon: <Undo2 size={17} /> },
          ],
        },
        { to: "/admin/invio-pratiche", label: "Invio pratiche", icon: <Send size={17} /> },
        { to: "/admin/utenze", label: "Utenze fornitore", icon: <KeyRound size={17} /> },
        { to: "/admin/affiliazioni", label: "Affiliazioni", icon: <UserPlus size={17} /> },
        { to: "/admin/qr-agenzie", label: "QR Self Service Clienti", icon: <QrCode size={17} /> },
        { to: "/admin/voci-menu", label: "Voci del menù", icon: <PanelsTopLeft size={17} /> },
        { to: "/admin/rete", label: "Rete", icon: <Network size={17} /> },
      ],
    });
  }

  function isGroupExpanded(item: NavItem): boolean {
    const childActive = item.children?.some((c) => c.to === location.pathname) ?? false;
    return manualExpanded[item.label] ?? childActive;
  }

  /** Una sezione richiudibile è chiusa finché non ci si lavora dentro: le sue
   *  voci sono le più rare e rubavano spazio alla navigazione di tutti i giorni. */
  function isSectionExpanded(section: { label?: string; items: NavItem[]; richiudibile?: boolean }): boolean {
    if (!section.richiudibile || !section.label) return true;
    const dentroLaSezione = section.items.some(
      (i) => i.to === location.pathname || i.children?.some((c) => c.to === location.pathname),
    );
    return manualExpanded[`sezione:${section.label}`] ?? dentroLaSezione;
  }

  const linkBase = "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors";

  const sidebarContent = (
    <aside className="w-[220px] bg-sidebar text-sidebar-foreground border-r border-white/10 flex flex-col h-full">
      <div className="px-5 py-4 border-b border-white/10">
        <img src="/logo.png" alt="AIEnergia" className="h-7 mx-auto brightness-0 invert" />
      </div>

      <nav data-tour="sidebar-nav" className="flex-1 min-h-0 p-3 space-y-0.5 overflow-y-auto">
        {/* Con le voci spente per agenzia un gruppo può restare vuoto: non si disegna, e nemmeno la
            sua linea, che altrimenti si sommerebbe a quella del gruppo dopo. */}
        {sections.filter((s) => s.items.length > 0).map((section, si) => {
          const sezioneAperta = isSectionExpanded(section);
          return (
          <div key={si}>
            {section.separata && si > 0 && <hr className="mx-3 my-2 border-white/10" />}
            {section.label && (section.richiudibile ? (
              <button
                type="button"
                aria-expanded={sezioneAperta}
                onClick={() =>
                  setManualExpanded((prev) => ({
                    ...prev,
                    [`sezione:${section.label}`]: !sezioneAperta,
                  }))
                }
                className="w-full flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#7fa384] hover:text-[#a9c2ab] transition-colors px-3 pt-4 pb-1.5 font-semibold"
              >
                <ChevronDown
                  size={13}
                  className={cn("transition-transform duration-200", !sezioneAperta && "-rotate-90")}
                />
                <span className="flex-1 text-left">{section.label}</span>
              </button>
            ) : (
              <div className="text-[10px] uppercase tracking-widest text-[#7fa384] px-3 pt-4 pb-1.5 font-semibold">
                {section.label}
              </div>
            ))}
            {sezioneAperta && section.items.map((item) => {
              if (item.children) {
                const expanded = isGroupExpanded(item);
                const childActive = item.children.some((c) => c.to === location.pathname);
                return (
                  <div key={item.label}>
                    <button
                      type="button"
                      aria-expanded={expanded}
                      onClick={() =>
                        setManualExpanded((prev) => ({ ...prev, [item.label]: !expanded }))
                      }
                      className={cn(
                        linkBase,
                        "w-full",
                        childActive
                          ? "text-[#EAF3E6] font-semibold"
                          : "text-[#a9c2ab] hover:bg-white/[0.06] hover:text-[#EAF3E6]"
                      )}
                    >
                      <span className={childActive ? "text-[#8DE05A]" : "text-[#7fa384]"}>
                        {item.icon}
                      </span>
                      <span className="flex-1 text-left">{item.label}</span>
                      <ChevronDown
                        size={15}
                        className={cn(
                          "text-[#7fa384] transition-transform duration-200",
                          expanded && "rotate-180"
                        )}
                      />
                    </button>
                    {expanded && (
                      <div className="pl-6">
                        {item.children.map((child) => (
                          <NavLink
                            key={child.to}
                            to={child.to!}
                            onClick={onMobileClose}
                            className={({ isActive }) =>
                              cn(
                                linkBase,
                                isActive
                                  ? "bg-brand/15 text-brand-light font-semibold"
                                  : "text-[#a9c2ab] hover:bg-white/[0.06] hover:text-[#EAF3E6]"
                              )
                            }
                          >
                            {({ isActive }) => (
                              <>
                                <span className={isActive ? "text-[#8DE05A]" : "text-[#7fa384]"}>
                                  {child.icon}
                                </span>
                                <span className="flex-1">{child.label}</span>
                              </>
                            )}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return item.href ? (
                <a
                  key={item.to}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-tour={item.tourKey ? `sidebar-${item.tourKey}` : undefined}
                  onClick={onMobileClose}
                  className={cn(linkBase, "text-[#a9c2ab] hover:bg-white/[0.06] hover:text-[#EAF3E6]")}
                >
                  <span className="text-[#7fa384]">{item.icon}</span>
                  {item.label}
                </a>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to!}
                  data-tour={item.tourKey ? `sidebar-${item.tourKey}` : undefined}
                  onClick={onMobileClose}
                  className={({ isActive }) =>
                    cn(
                      linkBase,
                      isActive
                        ? "bg-brand/15 text-brand-light font-semibold"
                        : "text-[#a9c2ab] hover:bg-white/[0.06] hover:text-[#EAF3E6]"
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className={isActive ? "text-[#8DE05A]" : "text-[#7fa384]"}>{item.icon}</span>
                      <span className="flex-1">{item.label}</span>
                      {item.badge !== undefined && item.badge > 0 && (
                        <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] text-center">
                          {item.badge > 9 ? "9+" : item.badge}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
          );
        })}
      </nav>

      {/* Il nome (col suo badge) sta su una riga tutta sua: condividerla con
          l'uscita lo troncava — «And… Supervisore Esci». Il piede resta di tre
          righe e la lista delle voci scorre quando non ci sta: è il baratto
          scelto il 2026-09-13. */}
      <div className="p-3 border-t border-white/10">
        <div className="text-xs text-[#8fae91] px-3 mb-1 flex items-center gap-2 flex-wrap">
          <span className="truncate" title={user.email || undefined}>
            {user.name?.trim() || (user.email || "").split("@")[0]}
          </span>
          {user.roles.includes("operatore_supervisore") && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-600 border border-amber-200"
              title="Accesso esteso a tutte le pratiche dell'agenzia"
            >
              Supervisore
            </span>
          )}
        </div>
        {isStaff && (
          <button
            onClick={() => {
              onMobileClose?.();
              setSegnalaAperto(true);
            }}
            className={cn(linkBase, "text-[#a9c2ab] hover:text-[#EAF3E6] hover:bg-white/[0.06] w-full text-left")}
          >
            <Flag size={17} className="text-[#7fa384] shrink-0" />
            Segnala un problema
          </button>
        )}
        <button
          onClick={logout}
          className={cn(linkBase, "text-[#a9c2ab] hover:text-[#EAF3E6] hover:bg-white/[0.06] w-full")}
        >
          <LogOut size={17} className="text-[#7fa384]" />
          Esci
        </button>
      </div>
    </aside>
  );

  return (
    <>
      {/* Desktop: fisso */}
      <div className="hidden md:block fixed left-0 top-0 h-screen z-40">
        {sidebarContent}
      </div>

      {/* Mobile: overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={onMobileClose}
          />
          <div className="relative h-full shadow-xl">
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Fuori dai due rami: una sola finestra, sopra il menù mobile. */}
      <SegnalaDialog open={segnalaAperto} onClose={() => setSegnalaAperto(false)} />
    </>
  );
}
